import { useId } from 'react';
import styled from 'styled-components';

import type { ReactNode } from 'react';

type Props = {
  /** One or two words, Title Case: Timing, Layout, Position, Animation. */
  title: string;
  children: ReactNode;
};

/**
 * A titled group of fields in an element settings panel
 * (docs/design-system.md § "Panel Fields": sections). The fields used to run
 * as one list held apart only by their labels' margins (PLAT-12137).
 *
 * No divider and no collapse: the title and `--spacing-lg` between sections
 * are the whole separation (the Line Diet).
 */
const PanelSection = ({ title, children }: Props) => {
  const titleId = useId();
  return (
    <Section aria-labelledby={titleId}>
      <Title id={titleId}>{title}</Title>
      {children}
    </Section>
  );
};

export default PanelSection;

/** Two short fields side by side in a section (Start | Duration). */
export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: var(--spacing-sm);
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;

  & + & {
    margin-top: var(--spacing-lg);
  }
`;

const Title = styled.h3`
  margin: 0;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  line-height: var(--leading-text-sm, 20px);
`;
